import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuthData, useMarkNeoIntroSeen } from '@/hooks/useAuthQueries';
import { useSpeak2MeKickoffStore } from '@/store/speak2meKickoffStore';
import { useSpeak2MeStore } from '@/store/speak2meStore';

const KICKOFF_MESSAGE = "I'm new to Vivport — please show me around.";

export function NeoIntroModal() {
  const location = useLocation();
  const { account } = useAuthData();
  const markSeen = useMarkNeoIntroSeen();
  const setPendingKickoff = useSpeak2MeKickoffStore((state) => state.setPendingKickoff);
  const setIsOpen = useSpeak2MeStore((state) => state.setIsOpen);
  const setOpenAsFreshChat = useSpeak2MeStore((state) => state.setOpenAsFreshChat);
  const [dismissed, setDismissed] = useState(false);

  if (location.pathname.startsWith('/subscribe')) {
    return null;
  }

  if (!account || account.neoIntroSeenAt !== null || dismissed) {
    return null;
  }

  const acknowledge = () => {
    setDismissed(true);
    markSeen.mutate();
  };

  const startTour = () => {
    setPendingKickoff(KICKOFF_MESSAGE);
    setOpenAsFreshChat(true);
    setIsOpen(true);
    acknowledge();
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) acknowledge();
      }}
    >
      <DialogContent className="sm:max-w-md" data-testid="neo-intro-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Welcome to Vivport
          </DialogTitle>
          <DialogDescription className="pt-2 text-left">
            You're all set up. Meet Neo — your AI operator. Neo can show you around: where your
            work lives, how your email and calendar connect, and how to put Vivport to work for
            you. It takes a few minutes, and you can steer or stop at any point. Prefer to look
            around on your own? You can ask Neo for a tour any time.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={acknowledge}
            disabled={markSeen.isPending}
            data-testid="neo-intro-skip-btn"
          >
            I'll explore on my own
          </Button>
          <Button onClick={startTour} disabled={markSeen.isPending} data-testid="neo-intro-tour-btn">
            Show me around
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
